import './App.css';
import logo from './assets/logo.svg';
import { useEffect, useRef } from 'react';

import estatutoSocialUrl from './assets/docs/estatuto-social-20260120.pdf';
import reglamentoSolidaridadUrl from './assets/docs/reglamento-solidaridad-20260314.pdf';
import tarifarioUrl from './assets/docs/tarifario-20260120.xlsx';
import nominaDirectivosUrl from './assets/docs/nomina-directivos-20260120.pdf';
import requisitosAsociacionUrl from './assets/docs/requisitos-asociacion-20260120.pdf';
import requisitosPrestamosUrl from './assets/docs/requisitos-prestamos-20260120.pdf';

const benefits = [
  {
    title: 'Ahorro y participación cooperativa',
    description: 'Aportes y ahorro que fortalecen el capital social de la cooperativa y permiten acceder a servicios y beneficios establecidos en el estatuto y reglamentos vigentes.',
  },
  {
    title: 'Créditos bajo normas claras',
    description: 'Líneas de crédito para consumo, vivienda, educación y otras necesidades, otorgadas conforme a políticas internas, con tasas y condiciones aprobadas por los órganos de gobierno.',
  },
  {
    title: 'Fondo de solidaridad con reglamento propio',
    description: 'Acceso a subsidios por salud, maternidad, incapacidad transitoria y fallecimiento, según condiciones, topes y requisitos establecidos en el Reglamento de Solidaridad vigente.',
  },
];

const services = [
  'Aportes y ahorro cooperativo para la conformación del capital social.',
  'Líneas de crédito para consumo, vivienda, educación y necesidades específicas.',
  'Créditos especiales sujetos a campañas y condiciones aprobadas por el Consejo de Administración.',
  'Fondo de solidaridad para eventos de salud, maternidad, incapacidad y fallecimiento, según reglamento.',
  'Servicios de consumo y convenios con entidades y proveedores.',
  'Educación cooperativa y orientación institucional para los socios.',
];

const documents = [
  {
    id: 'estatuto-social',
    title: 'Estatuto social',
    filename: 'estatuto-social.pdf',
    description: 'Normas que rigen la organización, derechos y deberes de los socios.',
    url: estatutoSocialUrl,
  },
  {
    id: 'beneficios-solidaridad',
    title: 'Beneficios de solidaridad',
    filename: 'beneficios-solidaridad.pdf',
    description: 'Reglamento de solidaridad y beneficios para socios que se encuentran al día en sus obligaciones.',
    url: reglamentoSolidaridadUrl,
  },
  {
    id: 'tarifario',
    title: 'Tasas y tarifario vigentes',
    filename: 'tarifario-20260120.xlsx',
    description: 'Detalle de tasas y cargos aplicables a productos y servicios.',
    url: tarifarioUrl,
  },
  {
    id: 'nomina-directivos',
    title: 'Nómina de directivos',
    filename: 'nomina-directivos.pdf',
    description: 'Listado actualizado de los miembros del consejo directivo y comités.',
    url: nominaDirectivosUrl,
  },
  {
    id: 'requisitos-asociacion',
    title: 'Requisitos para asociarse',
    filename: 'requisitos-asociacion.pdf',
    description: 'Documentación y pasos necesarios para convertirse en socio.',
    url: requisitosAsociacionUrl,
  },
  {
    id: 'requisitos-prestamos',
    title: 'Requisitos para préstamos',
    filename: 'requisitos-prestamos.pdf',
    description: 'Documentación y pasos necesarios para solicitar un préstamo.',
    url: requisitosPrestamosUrl,
  },
];

const leadership = [
  {
    name: 'Consejo de Administración',
    detail:
        'Órgano responsable de la dirección, administración y ejecución de las decisiones de la cooperativa.',
  },
  {
    name: 'Junta de Vigilancia',
    detail:
        'Fiscaliza la gestión del Consejo de Administración y el cumplimiento del Estatuto y reglamentos.',
  },
  {
    name: 'Tribunal Electoral Independiente',
    detail:
        'Garantiza los procesos electorales internos y la transparencia democrática.',
  },
  {
    name: 'Comité de Crédito',
    detail:
        'Evalúa y resuelve las solicitudes de crédito conforme a las políticas y reglamentos vigentes.',
  },
  {
    name: 'Comité de Solidaridad',
    detail:
        'Administra y evalúa los beneficios del fondo de solidaridad según su reglamento específico.',
  },
  {
    name: 'Comité de Educación',
    detail:
        'Promueve la educación cooperativa y la formación de los socios.',
  },
];

const news = [
  {
    title: 'Asamblea General 2026',
    description: 'Convocatoria a Asamblea Ordinaria para el mes de abril 2026. Uno de los requisitos de participación con voz y voto es estar al día con las obligaciones hasta el 20/02/2026.',
  },
  {
    title: 'Cambio de números de teléfono',
    description: 'Debido a inconvenientes técnicos por parte de la empresa Copaco S.A., se realizó la cancelación de los números de línea baja.',
  },
  {
    title: 'Horarios de fin de año',
    description: 'Miércoles 24 y martes 30, la atención será hasta las 12:00. Retorno al horario habitual el lunes 29 y viernes 2.',
  },
];

function App() {
  const headerRef = useRef(null);

  useEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl) return;

    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      const delta = y - lastY;

      // "shrink" state (kept as a class on header)
      headerEl.classList.toggle('is-scrolled', y > 12);

      // hide/show on direction with a small deadband to prevent jitter
      const goingDown = delta > 6;
      const goingUp = delta < -6;

      if (y <= 96) {
        headerEl.classList.remove('is-hidden');
      } else if (goingDown) {
        headerEl.classList.add('is-hidden');
      } else if (goingUp) {
        headerEl.classList.remove('is-hidden');
      }

      lastY = y;
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="app">
      <header ref={headerRef} className="site-header">
        <div className="container header-content">
          <div className="brand">
            <img src={logo} alt="Logo de la cooperativa" />
            <div>
              <p className="brand-name">Cooperativa Israelita Ltda.</p>
              <p className="brand-tagline">Juntos crecemos</p>
            </div>
          </div>
          <nav className="site-nav" aria-label="Principal">
            <a href="#about">Quiénes somos</a>
            <a href="#membership">Asociarse</a>
            <a href="#services">Servicios</a>
            <a href="#governance">Gobierno</a>
            <a href="#documents">Documentos</a>
            <a href="#contact" className="button button-outline">
              Contáctanos
            </a>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="container hero-content">
            <div>
              <p className="eyebrow">Cooperativa multiactiva de ahorro, crédito y servicios</p>
              <h1>Impulsamos el bienestar económico y social.</h1>
              <p className="hero-text">
                Somos una cooperativa multiactiva que promueve el ahorro, el acceso a crédito y la ayuda mutua.
                Trabajamos para brindar a nuestros socios respaldo financiero, beneficios solidarios y servicios
                que acompañan su bienestar y el de sus familias.
              </p>
              <div className="hero-actions">
                <a href="#membership" className="button">
                  Conocé cómo asociarte
                </a>
                <a href="#contact" className="button button-ghost">
                  Solicitar información
                </a>
              </div>
            </div>
            <div className="hero-card">
              <h2>¿Por qué la cooperativa?</h2>
              <ul>
                <li>Fondo de solidaridad regulado y transparente.</li>
                <li>Atención cercana y gestión responsable.</li>
                <li>Gobierno participativo y control institucional.</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="about" className="section">
          <div className="container section-grid">
            <div>
              <h2>Quiénes somos</h2>
              <p>
                La Cooperativa Israelita Ltda. es una cooperativa multiactiva de ahorro, crédito, consumo y servicios,
                constituida en la ciudad de Asunción y reconocida conforme a la legislación cooperativa paraguaya.
              </p>
              <p>
                Nuestra finalidad es mejorar las condiciones económicas, sociales y culturales de los socios y sus
                familias, fomentando el ahorro, la ayuda mutua, la educación cooperativa y el acceso a servicios
                financieros y solidarios.
              </p>
              <p>
                La cooperativa desarrolla sus actividades dentro del marco legal vigente, con una gestión democrática,
                transparente y participativa, y con un fuerte compromiso con el bienestar comunitario.
              </p>
            </div>
            <div className="highlight">
              <h3>Misión</h3>
              <p>
                Brindar a los socios servicios de ahorro, crédito, consumo y apoyo solidario, promoviendo la educación
                cooperativa, la responsabilidad financiera y la ayuda mutua, con el objetivo de contribuir al bienestar
                económico y social de las familias asociadas.
              </p>
              <h3>Visión</h3>
              <p>
                Consolidarnos como una cooperativa sólida, confiable y socialmente responsable, reconocida por su
                gestión transparente, su cercanía con los socios y su capacidad de brindar soluciones cooperativas
                sostenibles en el tiempo.
              </p>
            </div>
          </div>
        </section>

        <section className="section section-muted" id="benefits">
          <div className="container">
            <h2>Beneficios principales</h2>
            <div className="cards">
              {benefits.map((benefit) => (
                <article className="card" key={benefit.title}>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="membership" className="section">
          <div className="container section-grid">
            <div>
              <h2>Cómo asociarse</h2>
              <p>
                Invitamos a las personas interesadas a conocer los requisitos y el proceso de ingreso. La
                afiliación es sencilla y contamos con acompañamiento durante cada paso.
              </p>
              <ul className="checklist">
                <li>Presentar formulario de solicitud.</li>
                <li>Fotocopia de cedula de identidad.</li>
                <li>Una factura de Servicios Públicos Ande o Essap.</li>
                <li>Certificado de trabajo o 3 últimos formularios de IVA.</li>
                <li>Haber cumplido 18 años ser legalmente capaz.</li>
              </ul>
              <p>
                La solicitud debe ser aprobada por el Consejo de Administración.
              </p>
            </div>
            <div className="info-panel">
              <h3>Horarios de atención</h3>
              <p>Lunes a viernes · 7:30 a.m. – 4:00 p.m.</p>
              <h3>Canales de afiliación</h3>
              <p>Presencial y virtual con acompañamiento de nuestro equipo.</p>
              <a href="#contact" className="button button-outline">
                Agenda una visita
              </a>
            </div>
          </div>
        </section>

        <section id="services" className="section section-muted">
          <div className="container">
            <h2>Servicios y líneas cooperativas</h2>
            <p className="section-intro">
              La Cooperativa Israelita Ltda. brinda servicios de ahorro, crédito, consumo y solidaridad, orientados
              a satisfacer las necesidades económicas y sociales de sus socios, conforme a su estatuto y reglamentos
              vigentes.
            </p>
            <ul className="service-list">
              {services.map((service) => (
                <li key={service}>{service}</li>
              ))}
            </ul>
          </div>
        </section>

        <section id="governance" className="section">
          <div className="container">
            <h2>Gobierno y transparencia</h2>
            <p className="section-intro">
              La cooperativa cuenta con órganos de dirección y comités especializados que garantizan la
              transparencia en la gestión y la participación democrática.
            </p>
            <div className="cards">
              {leadership.map((item) => (
                <article className="card" key={item.name}>
                  <h3>{item.name}</h3>
                  <p>{item.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="documents" className="section section-muted">
          <div className="container">
            <h2>Documentos institucionales</h2>
            <p className="section-intro">
              Contamos con documentación actualizada para respaldar nuestra gestión y las condiciones de
              afiliación y servicios. Podés solicitar copias digitales a través de nuestros canales.
            </p>
            <div className="document-grid">
              {documents.map((doc) => (
                <div className="document-item" key={doc.id}>
                  <div className="doc-meta">
                    <span className="doc-title">{doc.title}</span>
                    {doc.description && <small className="doc-desc">{doc.description}</small>}
                  </div>
                  <div className="doc-actions">
                    <a
                      href={doc.url}
                      className="button button-outline"
                      download={doc.filename}
                      aria-label={`Descargar ${doc.title}`}
                    >
                      Descargar
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="news" className="section">
          <div className="container">
            <h2>Novedades y actividades</h2>
            <div className="cards">
              {news.map((item) => (
                <article className="card" key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="section section-muted">
          <div className="container contact">
            <div>
              <h2>Contacto</h2>
              <p>
                Estamos disponibles para resolver dudas, acompañar afiliaciones y brindar información sobre
                nuestros servicios.
              </p>
              <div className="contact-info">
                <p>
                  <strong>Dirección:</strong> 15 de Agosto N°1017. Edificio Drelichman, primer piso. Asunción, Paraguay
                </p>
                <p>
                  <strong>Teléfono:</strong>{' '}
                  <a
                    href="https://wa.me/595992209951"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Contactar por WhatsApp al +595 992 209951"
                  >
                    (+595) 992 209951
                  </a>{' '}
                  y <a href="tel:+595991770850">(+595) 991 770850</a>
                </p>
                <p>
                  <strong>Correo:</strong> <a href="mailto:coopis@coopis.com.py">coopis@coopis.com.py</a>
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-content">
          <div>
            <p className="footer-title">Cooperativa Israelita</p>
            <p>Juntos crecemos</p>
          </div>
          <div className="footer-links">
            <a href="#about">Quiénes somos</a>
            <a href="#membership">Asociarse</a>
            <a href="#documents">Documentos</a>
            <a href="#contact">Contacto</a>
          </div>
          <div className="footer-meta">
            <p>Horario: lunes a viernes de 7:30 a 16:00 horas.</p>
            <p>© {new Date().getFullYear()} Cooperativa Israelita. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
